import { ChangeEvent, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

type FormStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

type FormValues = {
  company: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  phoneAlt: string;
};

const SUBJECT_OPTIONS = [
  { value: '', label: '選択してください' },
  { value: '営業支援について', label: '営業支援について' },
  { value: '営業支援コンサルティングについて', label: '営業支援コンサルティングについて' },
  { value: '各種コンサルティングについて', label: '各種コンサルティングについて' },
  { value: '営業代行について', label: '営業代行について' },
  { value: '販売代行について', label: '販売代行について' },
  { value: 'システム開発について', label: 'システム開発について' },
  { value: '料金プランについて', label: '料金プランについて' },
  { value: 'その他', label: 'その他' },
];

const INITIAL_VALUES: FormValues = {
  company: '',
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  phoneAlt: '',
};

export default function ContactPage() {
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_VALUES);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const subjectLabel = useMemo(() => {
    return SUBJECT_OPTIONS.find((option) => option.value === formValues.subject)?.label || '未選択';
  }, [formValues.subject]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormValues(INITIAL_VALUES);
    setPrivacyAgreed(false);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formValues.phoneAlt.trim() !== '') {
      setFormStatus('success');
      return;
    }

    if (!privacyAgreed) {
      setErrorMsg('プライバシーポリシーに同意してください。');
      setFormStatus('error');
      return;
    }

    setErrorMsg('');
    setFormStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setFormStatus('submitting');
    setErrorMsg('');

    try {
      const params = new URLSearchParams({
        company: formValues.company,
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone,
        subject: subjectLabel === '選択してください' ? '' : subjectLabel,
        message: formValues.message,
        privacy: privacyAgreed ? '同意済み' : '',
      });

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: params.toString(),
      });

      const responseText = (await response.text()).trim();

      if (response.ok && responseText === 'OK') {
        setFormStatus('success');
        resetForm();
      } else {
        setErrorMsg('送信に失敗しました。お急ぎの場合はメールにてお問い合わせください。');
        setFormStatus('error');
      }
    } catch {
      setErrorMsg('送信に失敗しました。お急ぎの場合はメールにてお問い合わせください。');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section className="w-full pt-28 pb-20 md:pt-32 md:pb-28 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-primary-600 text-sm font-medium tracking-widest mb-3">CONTACT</p>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                お問い合わせ
              </h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                サービスに関するご質問やご相談は、以下のフォームよりお気軽にお寄せください。
                内容を確認の上、担当者より折り返しご連絡いたします。
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8"
              noValidate
            >
              <div className="flex flex-col gap-5">
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    会社名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-company"
                    name="company"
                    value={formValues.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="例：株式会社〇〇"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      お名前 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      value={formValues.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="例：山田 太郎"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      メールアドレス <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={formValues.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="例：info@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="contact-phone"
                    name="phone"
                    value={formValues.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="例：03-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    お問い合わせ種別 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={formValues.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                  >
                    {SUBJECT_OPTIONS.map((option) => (
                      <option key={option.value || 'empty'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    お問い合わせ内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formValues.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="お問い合わせ内容を具体的にご記入ください（500文字以内）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">500文字以内でご入力ください</p>
                </div>

                <div className="honeypot-field sr-only" aria-hidden="true">
                  <input
                    type="text"
                    name="phoneAlt"
                    value={formValues.phoneAlt}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>

                <div className="pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyAgreed}
                      onChange={(e) => setPrivacyAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
                    />
                    <span className="text-sm text-foreground-600 leading-relaxed">
                      <Link to="/privacy" className="text-primary-600 underline hover:text-primary-700">
                        プライバシーポリシー
                      </Link>
                      に同意します
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3.5 text-sm font-semibold rounded-md whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      送信中...
                    </span>
                  ) : (
                    '送信する'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                  <i className="ri-user-line" />
                </div>
                <p className="text-xs text-foreground-500 mb-1">代表者</p>
                <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">笹川俊</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                  <i className="ri-mail-line" />
                </div>
                <p className="text-xs text-foreground-500 mb-1">メールでのお問い合わせ</p>
                <a href="mailto:banking@dream-dreaminc.com" className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors break-all">
                  banking@dream-dreaminc.com
                </a>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                  <i className="ri-map-pin-line" />
                </div>
                <p className="text-xs text-foreground-500 mb-1">所在地</p>
                <p className="text-sm font-medium text-foreground-900">横浜市西区北幸２丁目１０番４８号</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {formStatus === 'confirm' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 md:px-8 py-6 border-b border-background-200">
              <p className="text-xs tracking-[0.2em] text-primary-600 mb-2">CONFIRM</p>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">入力内容の確認</h3>
              <p className="text-sm text-foreground-500 mt-2">送信前に内容をご確認ください。</p>
            </div>

            <div className="px-6 md:px-8 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">会社名</p>
                  <p className="text-sm text-foreground-800 break-words">{formValues.company}</p>
                </div>
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">お名前</p>
                  <p className="text-sm text-foreground-800 break-words">{formValues.name}</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">メールアドレス</p>
                  <p className="text-sm text-foreground-800 break-all">{formValues.email}</p>
                </div>
                <div className="rounded-2xl bg-background-100 px-4 py-3">
                  <p className="text-xs text-foreground-400 mb-1">電話番号</p>
                  <p className="text-sm text-foreground-800 break-words">{formValues.phone || '未入力'}</p>
                </div>
              </div>
              <div className="rounded-2xl bg-background-100 px-4 py-3">
                <p className="text-xs text-foreground-400 mb-1">お問い合わせ種別</p>
                <p className="text-sm text-foreground-800 break-words">{subjectLabel === '選択してください' ? '未選択' : subjectLabel}</p>
              </div>
              <div className="rounded-2xl bg-background-100 px-4 py-3">
                <p className="text-xs text-foreground-400 mb-1">お問い合わせ内容</p>
                <p className="text-sm text-foreground-800 whitespace-pre-wrap break-words">{formValues.message}</p>
              </div>
            </div>

            <div className="px-6 md:px-8 py-5 border-t border-background-200 flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setFormStatus('idle')}
                className="px-5 py-3 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="px-5 py-3 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
              >
                送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {formStatus === 'submitting' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-5">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
            </div>
            <h3 className="font-heading text-2xl text-foreground-900 mb-2">送信中</h3>
            <p className="text-sm text-foreground-500">お問い合わせ内容を送信しています。</p>
          </div>
        </div>
      )}

      {formStatus === 'success' && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-accent-50 flex items-center justify-center mb-5">
              <i className="ri-checkbox-circle-line text-2xl text-accent-700"></i>
            </div>
            <h3 className="font-heading text-2xl text-foreground-900 mb-2">送信完了</h3>
            <p className="text-sm text-foreground-500 mb-6">
              お問い合わせを受け付けました。内容を確認のうえ順次ご連絡いたします。
            </p>
            <button
              type="button"
              onClick={() => setFormStatus('idle')}
              className="px-6 py-3 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {formStatus === 'error' && errorMsg && (
        <div className="fixed inset-0 z-[60] bg-black/55 px-4 py-6 flex items-center justify-center">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl px-8 py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-5">
              <i className="ri-error-warning-line text-2xl text-red-600"></i>
            </div>
            <h3 className="font-heading text-2xl text-foreground-900 mb-2">送信失敗</h3>
            <p className="text-sm text-foreground-500 mb-6">{errorMsg}</p>
            <button
              type="button"
              onClick={() => setFormStatus('idle')}
              className="px-6 py-3 rounded-full bg-foreground-900 text-white text-sm font-medium hover:bg-foreground-800 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
